import { useEffect, useState } from 'react'
import { shopBrandingGetService } from 'src/api/shop'

/**
 * The workspace branding (Workspace › Branding): logo, primary color and font.
 * Landing page previews fall back to it as the learner app does. It is kept
 * between screens and asked for again whenever a preview opens, so a change
 * made in Branding shows the next time a preview is opened.
 */
let branding = null
let request = null
// Whether an answer has arrived (the logo may still be null: no logo).
let loaded = false
// Hooks showing the branding, told when Branding saves a change.
const listeners = new Set()

/** Called by Workspace › Branding after a save, so every view updates. */
export const setWorkspaceBranding = next => {
  branding = { ...(branding || {}), ...next }
  loaded = true
  listeners.forEach(listener => listener(branding))
}

const fetchBranding = () => {
  if (!request) {
    request = shopBrandingGetService()
      .then(response => {
        branding = response?.data || null
        return branding
      })
      .catch(() => branding)
      .finally(() => {
        loaded = true
        request = null
      })
  }
  return request
}

/** The workspace logo, asked for again (an editor opening sees changes). */
export const loadWorkspaceLogo = () =>
  fetchBranding().then(value => value?.logo || null)

export const getWorkspaceLogo = () => branding?.logo || null

/** Landing page data with the workspace logo when it has none. */
export const withWorkspaceLogo = landingPageData =>
  landingPageData && !landingPageData.logo && getWorkspaceLogo()
    ? { ...landingPageData, logo: getWorkspaceLogo() }
    : landingPageData

/** The workspace branding, refreshed on mount; null until known. */
export const useWorkspaceBranding = () => {
  const [value, setValue] = useState(branding)
  useEffect(() => {
    let active = true
    fetchBranding().then(next => active && setValue(next))
    listeners.add(setValue)
    return () => {
      active = false
      listeners.delete(setValue)
    }
  }, [])
  return value
}

/** The workspace logo; re-renders once it has loaded. */
export const useWorkspaceLogo = () => useWorkspaceBranding()?.logo || null

/**
 * `{ logo, loaded }`: like useWorkspaceLogo, but also says whether the answer
 * has arrived, so a header can wait instead of swapping name for logo.
 */
export const useWorkspaceLogoState = () => {
  const [state, setState] = useState({
    logo: branding?.logo || null,
    loaded,
  })
  useEffect(() => {
    let active = true
    fetchBranding().then(
      next => active && setState({ logo: next?.logo || null, loaded: true }),
    )
    return () => {
      active = false
    }
  }, [])
  return state
}

// Landing pages start with these, so they count as not chosen (as on the
// server's StyleFallback): the workspace's color and font show instead.
const DEFAULT_PRIMARY_COLOR = '#015FFB'
const DEFAULT_FONT = 'Inter'

/**
 * Inter picked on purpose. Pages are created with "Inter", which stands for
 * "not chosen" (the workspace branding's font shows), so a page that wants
 * Inter itself stores this instead. Still valid CSS for Inter; the backend
 * (StyleFallback) and the helpers below turn it back into "Inter".
 */
export const CHOSEN_INTER = 'Inter, sans-serif'

/** The font family to use for a stored font value. */
export const fontFamilyOf = font => (font === CHOSEN_INTER ? DEFAULT_FONT : font)

const isChosen = (value, defaultValue) =>
  Boolean(value) &&
  String(value).trim().toLowerCase() !== defaultValue.toLowerCase()

/**
 * The color a page shows: its own when chosen, else the workspace branding's
 * (from the loaded branding; null until it loads, then the page's own).
 */
export const resolvePrimaryColor = color =>
  !isChosen(color, DEFAULT_PRIMARY_COLOR) &&
  isChosen(branding?.primaryColor, DEFAULT_PRIMARY_COLOR)
    ? branding.primaryColor
    : color || DEFAULT_PRIMARY_COLOR

/**
 * The font a page or course shows: its own when chosen, else the workspace
 * branding's (from the loaded branding), else Inter.
 */
export const resolveFont = font =>
  !isChosen(font, DEFAULT_FONT) && isChosen(branding?.font, DEFAULT_FONT)
    ? branding.font
    : fontFamilyOf(font) || DEFAULT_FONT

/** Whether a font was picked (anything but empty or the default Inter). */
export const isFontChosen = font => isChosen(font, DEFAULT_FONT)

/**
 * Landing page data with the workspace primary color and font where the page
 * keeps the defaults, as learners see a path or portal page.
 */
export const withWorkspaceStyle = (landingPageData, workspace) => {
  if (!landingPageData || !workspace) return landingPageData
  const primaryColor =
    !isChosen(landingPageData.primaryColor, DEFAULT_PRIMARY_COLOR) &&
    isChosen(workspace.primaryColor, DEFAULT_PRIMARY_COLOR)
      ? workspace.primaryColor
      : landingPageData.primaryColor
  const font =
    !isChosen(landingPageData.font, DEFAULT_FONT) &&
    isChosen(workspace.font, DEFAULT_FONT)
      ? workspace.font
      : fontFamilyOf(landingPageData.font)
  return primaryColor === landingPageData.primaryColor &&
    font === landingPageData.font
    ? landingPageData
    : { ...landingPageData, primaryColor, font }
}

/**
 * The color and font a learning path preset saves: a preset with the system
 * defaults (Default) takes the workspace branding instead, so a new path
 * starts in the workspace's style.
 */
export const pathPresetStyle = (template, workspace) => {
  const { primaryColor, font } = withWorkspaceStyle(template, workspace)
  return { primaryColor, font }
}
